// Atlas Dashboard — API Client
// A wrapper around fetch() that handles authentication, error handling, and response parsing.
// All API calls from the dashboard go through this client.

import type { ApiResponse } from "@atlas/shared";

/** Base URL for the Atlas API. */
const API_BASE_URL = "https://atlas-api.crawwwlr-mail.workers.dev";

/** localStorage key for the JWT auth token */
const AUTH_TOKEN_KEY = "atlas_auth_token";

/**
 * Gets the stored authentication token (if any).
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY) as string | null;
}

/**
 * Sets the authentication token (after login).
 */
export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

/**
 * Clears the authentication token (on logout).
 */
export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

/**
 * Checks if the user is authenticated (has a token).
 */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

/**
 * Makes an API request with authentication and error handling.
 *
 * @param path - The API path (e.g., "/api/emails" or "/api/contacts/abc123")
 * @param options - Fetch options (method, body, etc.)
 * @returns The typed response data, or throws an error with a plain-English message
 */
export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getAuthToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      // Token expired or missing — clear it and redirect to login
      clearAuthToken();
      throw new Error("Your session has expired. Please log in again.");
    }

    const body: ApiResponse<T> = await response.json();

    if (!body.success || body.error) {
      throw new Error(body.error || "Something went wrong. Please try again.");
    }

    return body.data as T;
  } catch (error) {
    if (error instanceof Error) {
      // Re-throw known errors with their plain-English message
      throw error;
    }
    // Network errors, CORS errors, etc.
    throw new Error(
      "Could not connect to Atlas. Please check your internet connection and try again.",
    );
  }
}

// ─── Convenience methods for common HTTP verbs ───

export const api = {
  get: <T>(path: string) => apiRequest<T>(path, { method: "GET" }),
  post: <T>(path: string, data?: unknown) =>
    apiRequest<T>(path, { method: "POST", body: data ? JSON.stringify(data) : undefined }),
  put: <T>(path: string, data?: unknown) =>
    apiRequest<T>(path, { method: "PUT", body: data ? JSON.stringify(data) : undefined }),
  patch: <T>(path: string, data?: unknown) =>
    apiRequest<T>(path, { method: "PATCH", body: data ? JSON.stringify(data) : undefined }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: "DELETE" }),
};